import { ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/core/PageContainer";
import { Title } from "@/components/core/Title";
import { useMemberDetails } from "@/hooks/useMemberDetails";
import { ErrorState } from "@/components/core/ErrorState";
import { MemberDetailsSkeleton } from "@/components/blocks/MemberDetails/MemberDetailsSkeleton";
import { PaymentSummary } from "@/components/blocks/ChargeMember/PaymentSummary";
import { OtpVerification } from "@/components/blocks/ChargeMember/OtpVerification";
import { PaymentSuccess } from "@/components/blocks/ChargeMember/PaymentSuccess";
import { ChargeFormStep } from "@/components/blocks/ChargeMember/ChargeFormStep";
import { useChargeMemberFlow } from "@/hooks/useChargeMemberFlow";
import { ChargeMemberStep } from "@/stores/useChargeMemberStore";
import { convertToMonetaryAmount } from "@/utils/numbers.utils";
import { useHideBottomNav } from "@/hooks/useHideBottomNav";
import { useDisableLocationSelection } from "@/hooks/useDisableLocationSelection";

export function ChargeMember() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Hide bottom nav and disable location selection immediately, even during loading
  useHideBottomNav(true);
  useDisableLocationSelection(true);
  
  const { memberDetails, isLoading, error, fetchMemberDetails } =
    useMemberDetails(memberId || "");
  
  // Use the charge member flow hook to manage state and actions
  const {
    currentStep,
    handleSubmit,
    handleBack,
    handleRequestOtp,
    handleVerificationSuccess,
    handleDone,
    paymentData,
    requestAmount,
    requestTip,
    otpPaymentId,
    successData,
    isSubmittingPayment,
  } = useChargeMemberFlow(memberId || "");

  if (!memberId) {
    // If no memberId found, redirect to members page
    navigate("/members");
    return null;
  }

  if (error) {
    return (
      <ErrorState
        error={error}
        title={t("customers.memberLoadError")}
        onRetry={() => fetchMemberDetails()}
      />
    );
  }

  if (isLoading) {
    return <MemberDetailsSkeleton isChargeMember />;
  }

  if (!memberDetails) {
    return (
      <div className="text-center text-muted-foreground">
        {t("customers.noMemberFound")}
      </div>
      )
  }

  // Define step components using an object mapping
  const stepComponents: Record<ChargeMemberStep, () => ReactNode> = {
    [ChargeMemberStep.FORM]: () => (
      <ChargeFormStep
        memberDetails={memberDetails}
        memberId={memberId}
        onSubmit={handleSubmit}
        isLoading={isSubmittingPayment}
      />
    ),
    
    [ChargeMemberStep.SUMMARY]: () => {
      if (!paymentData) return null;
      return (
        <PaymentSummary 
          data={paymentData} 
          requestAmount={requestAmount}
          requestTip={requestTip}
          currency={memberDetails.currency}
          onBack={handleBack}
          onRequestOtp={handleRequestOtp}
        />
      );
    },
    
    [ChargeMemberStep.VERIFICATION]: () => {
      if (!paymentData || !otpPaymentId) return null;
      return (
        <OtpVerification 
          otpPaymentId={otpPaymentId}
          memberId={paymentData.memberId}
          locationId={paymentData.locationId}
          memberPhoneNumber={memberDetails.member.phoneNumber}
          amountToPay={convertToMonetaryAmount(requestAmount)}
          tipAmount={convertToMonetaryAmount(requestTip)}
          note={paymentData.note || ""}
          onBack={handleBack}
          onSuccess={handleVerificationSuccess}        
          onResendOtp={handleRequestOtp}
        />
      );
    },
    
    [ChargeMemberStep.SUCCESS]: () => {
      if (!successData) return null;
      return (
        <PaymentSuccess
          data={successData}
          paymentMethodLast4={paymentData?.paymentMethodLast4 || ""}
          currency={memberDetails.currency}
          onDone={handleDone}
        />
      );
    }
  };

  // Get the current component to render
  const CurrentStepComponent = stepComponents[currentStep];

  return (
    <PageContainer scrollable fullHeight className="pb-safe flex flex-col">
      <Title showBackButton useCloseButton fallbackUrl="/members" refreshAction={currentStep === ChargeMemberStep.FORM ? () => fetchMemberDetails() : undefined}>
        {t("charge.title")}
      </Title>

      <div className="space-y-6 flex flex-col flex-1">
        <CurrentStepComponent />
      </div>
    </PageContainer>
  );
}
